/**
 * Does the Morning Brief promo (<DailyBriefPromo/>) show? ONE rule, used by the server paint AND the client fetch.
 *
 * 🚨 WHY THE SERVER DECIDES IT (register daily-brief-promo-pops-in-above-chart-1105). The card used to wait on three
 * client fetches and then insert itself ABOVE the /chart grid, pushing the chart down after first paint: signed-in
 * /chart RUM rows targeting div.grid.grid-cols-1.gap-6 with dy 128-191px (6 on 11-05), and a prod-build A/B at 1440
 * read 0.0532 on the grid with the card against no grid entry without it. The root layout now answers it from the
 * same reads the three endpoints make, so the card is in the first HTML or not at all. The client fetch stays as
 * the fallback (a layout read that timed out) and as a revalidation.
 *
 * ⛔ No 'use client' here: the root layout (a server component) imports it.
 */

export const BRIEF_PROMO_DISMISS_KEY = 'dt_brief_promo_dismissed'
/** Set on <html> before first paint when the card must not show (dismissed, share image, embed). */
export const BRIEF_PROMO_HIDE_ATTR = 'data-brief-hide'

export type BriefPromoInputs = {
  authed: boolean
  /** The brief can actually deliver (a real postal address is configured), so we never invite an opt-in it cannot fulfill. */
  available: boolean
  /** Already opted in. */
  enabled: boolean
  /** PremiumBannerState.kind; null or undefined when unknown (a failed read), which counts as silent. */
  premiumKind: string | null | undefined
  needsVerification: boolean
}

/**
 * NEVER TWO ASKS AT ONCE, AND THE MONEY ASK WINS. /alerts already carries <PremiumInlineAsk/>, and nudge-freedom is a
 * stated benefit of subscribing, so this stands down whenever the money ask has anything to say, and while the verify
 * ask owns the screen (for an unverified user the premium banner deliberately reads 'none' so the verify ask can stand
 * alone; without this check the brief stacked under <VerifyInlineAsk/> for the highest-leak cohort).
 * ⚠ first_session_trial renders ONLY on /alerts (PremiumInlineAsk maps it to none elsewhere), so it silences the brief
 * on /alerts alone (review 2026-10-21, stripe-starts-happen-day0-1021).
 * ⚠ A FAILED money or verify read must not suppress the offer: unknown counts as silent.
 */
export function briefPromoShows(i: BriefPromoInputs, onAlerts: boolean): boolean {
  const k = i.premiumKind
  const moneyAskActive = !!k && k !== 'none' && (k !== 'first_session_trial' || onAlerts)
  if (moneyAskActive || i.needsVerification) return false
  return i.authed && i.available && !i.enabled
}

/** Both surface answers, because the root layout does not know the path and the verdict differs on /alerts. */
export type BriefPromoInitial = { alerts: boolean; elsewhere: boolean }

/**
 * What the root layout hands the card. Signed out: it never shows, decided. Signed in with every read answered: the
 * server verdict. Signed in with any read missing (failed or over the layout budget): null, and the card falls back to
 * fetching after mount exactly as before.
 */
export function briefPromoInitialFromReads(r: {
  signedIn: boolean
  available: boolean
  premiumKind: string | undefined
  needsVerification: boolean | undefined
  enabled: boolean | undefined
}): BriefPromoInitial | null {
  if (!r.signedIn) return { alerts: false, elsewhere: false }
  if (r.premiumKind === undefined || r.needsVerification === undefined || r.enabled === undefined) return null
  const i: BriefPromoInputs = { authed: true, available: r.available, enabled: r.enabled, premiumKind: r.premiumKind, needsVerification: r.needsVerification }
  return { alerts: briefPromoShows(i, true), elsewhere: briefPromoShows(i, false) }
}

/**
 * Inline <head> script: marks <html> when the server-painted card must not show, before first paint. The server cannot
 * read localStorage (a dismissed card) or know ?shot=1 / ?embed=1 (the chart page learns those in an effect), and
 * removing an already-painted card would be a layout shift of its own. globals.css hides [data-brief-promo] under it.
 */
export function briefPromoPrePaintScript(): string {
  return `(function(){try{var p=new URLSearchParams(location.search);var d=false;try{d=!!localStorage.getItem('${BRIEF_PROMO_DISMISS_KEY}')}catch(x){}`
    + `if(d||p.get('shot')==='1'||p.get('embed')==='1')document.documentElement.setAttribute('${BRIEF_PROMO_HIDE_ATTR}','')}catch(x){}})()`
}

/**
 * The client revalidation after mount. A FAILED daily-brief read keeps what is painted: hiding a painted card on a
 * network blip would be a layout shift and a lost offer. An answered read applies the same rule as the server, so in
 * steady state it agrees with the server paint and nothing moves.
 */
export function briefPromoAfterFetch(
  current: boolean,
  d: { authed?: boolean; available?: boolean; enabled?: boolean } | null,
  premiumKind: string | null | undefined,
  needsVerification: boolean,
  onAlerts: boolean,
): boolean {
  if (!d) return current
  return briefPromoShows({ authed: !!d.authed, available: !!d.available, enabled: !!d.enabled, premiumKind, needsVerification }, onAlerts)
}
